import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';
import type { TestRun, CreateTestRunRequest, DatasheetPreview } from '../types';

type TestRunPaginatedResponse = {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  data: TestRun[];
};

const testRunKeys = {
  all: ['testRuns'] as const,
  byScript: (scriptId: string, page: number, pageSize: number) =>
    ['testRuns', 'script', scriptId, page, pageSize] as const,
  byId: (scriptId: string, runId: string) =>
    ['testRuns', scriptId, runId] as const
};

export const useTestRunsQuery = (scriptId: string, page = 1, pageSize = 20) => {
  return useQuery<TestRunPaginatedResponse, Error>({
    queryKey: testRunKeys.byScript(scriptId, page, pageSize),
    queryFn: async () => {
      const response = await bffApiClient.get<TestRunPaginatedResponse>(
        `/api/mqtt-scripts/${scriptId}/runs`,
        { params: { pageNumber: page, pageSize } }
      );
      return response.data;
    },
    enabled: !!scriptId,
    staleTime: 10_000
  });
};

export const useEquipmentRunsQuery = (
  equipmentGlobalId: string | null,
  page = 1,
  pageSize = 20
) => {
  return useQuery<TestRunPaginatedResponse, Error>({
    queryKey: ['testRuns', 'equipment', equipmentGlobalId, page, pageSize],
    queryFn: async () => {
      const response = await bffApiClient.get<TestRunPaginatedResponse>(
        '/api/runs',
        { params: { equipmentGlobalId, pageNumber: page, pageSize } }
      );
      return response.data;
    },
    enabled: !!equipmentGlobalId,
    staleTime: 10_000
  });
};

export const useTestRunQuery = (scriptId: string, runId: string) => {
  return useQuery<TestRun, Error>({
    queryKey: testRunKeys.byId(scriptId, runId),
    queryFn: async () => {
      const response = await bffApiClient.get<TestRun>(
        `/api/mqtt-scripts/${scriptId}/runs/${runId}`
      );
      return response.data;
    },
    enabled: !!scriptId && !!runId,
    staleTime: 5_000
  });
};

export const useCreateTestRun = (scriptId: string) => {
  const queryClient = useQueryClient();

  return useMutation<TestRun, Error, CreateTestRunRequest>({
    mutationFn: async dto => {
      const response = await bffApiClient.post<TestRun>(
        `/api/mqtt-scripts/${scriptId}/runs`,
        dto
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['testRuns', 'script', scriptId]
      });
    }
  });
};

export const useCancelTestRun = (scriptId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (runId: string) => {
      await bffApiClient.post(
        `/api/mqtt-scripts/${scriptId}/runs/${runId}/cancel`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['testRuns', 'script', scriptId]
      });
    }
  });
};

export const useDatasheetPreviewQuery = (scriptId: string) => {
  return useQuery<DatasheetPreview, Error>({
    queryKey: ['datasheetPreview', scriptId],
    queryFn: async () => {
      const response = await bffApiClient.get<DatasheetPreview>(
        `/api/mqtt-scripts/${scriptId}/datasheet-preview`
      );
      return response.data;
    },
    enabled: !!scriptId,
    staleTime: 10_000
  });
};
